import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import RecommendationCreate from '/@/components/recommendation-create';
import FieldSelect from '/@/components/recommendation-create/bayer/fields-step';
import { useCatalogProductById } from '/@/hooks/catalog';
import useDebounce from '/@/hooks/debounce/useDebounce';
// start steps components
import { useFieldById } from '/@/hooks/fields';
import { useGetUserNode } from '/@/hooks/nodes';
import {
    getClimatePatternChoices,
    useCornDensityCurveForProducts,
    useCreateRecommendationMutation,
    useEnvironments,
    useFertilizationCoefficientsForProducts,
    useSimplifiedDefaultCropConfig,
    useSoilMoistures,
    useYieldByEnvironmentForProducts,
} from '/@/hooks/recommendation';
import { useIsValidatedBayerUserType } from '/@/hooks/session';
// end steps components
import { _ } from '/@/locale';
import { getValidPlantingDates } from '/@/store/private-data/recommendations/selectors';
import { getAppInstance, getSessionCountry, getUser } from '/@/store/session/selectors';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { DensityRecommendationForm } from '/@/types/density-recommendation';
import type { Field } from '/@/types/field';
import type { Step } from '/@/types/recommendation-create';
import { isUserValidatedRV } from '/@/utils';
import { reverse } from '/@/utils/router';
import EnvironmentConditionsStep from '../components/environment-conditions';
import ProductsStep from '../components/products';
import CornDensityRecommendationWrapper from '../detail';

function DensityRecommendationCreate() {
    const [activeStep, setActiveStep] = useState<number>(0);
    const [field, setField] = useState<Field | undefined>(undefined);
    const navigate = useNavigate();
    const { search } = useLocation();
    const query = new URLSearchParams(search);

    const form = useForm<DensityRecommendationForm>({
        defaultValues: {
            fieldId: undefined,
            productId: undefined,
            plantingDate: '',
            soilMoisture: 0,
            climatePattern: undefined,
            environment: 0,
            successRate: 90,
            userDensity: 0,
            userNitrogen: 0,
        },
        mode: 'onChange',
    });

    // query params for form autocomplete
    const fieldId = Number(query.get('fieldId'));
    const plantingDateParam = query.get('plantingDate');
    const soilMoistureParam = query.get('soilMoisture');
    const climatePatternParam = query.get('climatePattern');
    const productIdParam = query.get('productId');
    const user = useSelector(getUser);
    const watchedFieldId = form.watch('fieldId');
    const watchedPlantingDate = form.watch('plantingDate');
    const watchedSoilMoisture = form.watch('soilMoisture');
    const watchedClimatePattern = form.watch('climatePattern');
    const watchedProductId = form.watch('productId');

    // Read URL params on mount and set form values only once
    useEffect(() => {
        const currentPlantingDate = form.getValues('plantingDate');
        const currentSoilMoisture = form.getValues('soilMoisture');
        const currentClimatePattern = form.getValues('climatePattern');
        const currentFieldId = form.getValues('fieldId');

        // Only set fieldId from URL if form doesn't have one already
        if (fieldId && !currentFieldId) {
            form.setValue('fieldId', fieldId);
            setActiveStep(1);
        }

        if (plantingDateParam && !currentPlantingDate) {
            form.setValue('plantingDate', plantingDateParam);
        }
        if (soilMoistureParam && !currentSoilMoisture) {
            form.setValue('soilMoisture', Number(soilMoistureParam));
        }
        if (climatePatternParam && !currentClimatePattern) {
            form.setValue('climatePattern', Number(climatePatternParam));
        }
        if (productIdParam && !form.getValues('productId')) {
            form.setValue('productId', Number(productIdParam));
        }
    }, [fieldId, plantingDateParam, soilMoistureParam, climatePatternParam, productIdParam, form]);

    const fieldQuery = useFieldById({ id: watchedFieldId || 0 }, { enabled: !!watchedFieldId });
    const country = useSelector(getSessionCountry);

    const soilMoisturesParams = useMemo(() => {
        const params = {
            countryCode: country?.code,
            climatePattern: watchedClimatePattern,
        };

        if (watchedPlantingDate) {
            (params as any).plantingDate = dayjs(watchedPlantingDate).format('YYYY-MM-DD');
        }

        return params;
    }, [watchedClimatePattern, watchedPlantingDate, country?.code]);

    const soilMoisturesQuery = useSoilMoistures(soilMoisturesParams, {
        enabled: !!watchedPlantingDate && !!watchedClimatePattern,
    });

    const climatePatternChoices = getClimatePatternChoices();
    const { data: product } = useCatalogProductById(
        {
            id: productIdParam,
            crop: 'corn',
        },
        { enabled: !!productIdParam }
    );

    const defaultCropConfig = useSimplifiedDefaultCropConfig({
        crop: 'corn',
        zones: fieldQuery?.data?.node?.zones || product?.zones || [],
        countryCode: fieldQuery?.data?.country?.code || country?.code,
    });

    // Memoize form setters to prevent infinite loops
    const setPlantingDate = useCallback(
        (value: string) => {
            form.setValue('plantingDate', value);
        },
        [form]
    );

    const setClimatePattern = useCallback(
        (value: number) => {
            form.setValue('climatePattern', value);
        },
        [form]
    );

    const setSoilMoisture = useCallback(
        (value: number) => {
            form.setValue('soilMoisture', value);
        },
        [form]
    );

    useEffect(() => {
        if (defaultCropConfig) {
            const currentPlantingDate = form.getValues('plantingDate');
            const currentClimatePattern = form.getValues('climatePattern');
            const newPlantingDate = (defaultCropConfig as any).plantingDate || '';
            const newClimatePattern = (defaultCropConfig as any).climatePattern || 0;

            // Set planting date if not already set
            if (!watchedPlantingDate && currentPlantingDate !== newPlantingDate) {
                setPlantingDate(newPlantingDate);
            }
            // Set climate pattern if not already set
            if (!watchedClimatePattern && currentClimatePattern !== newClimatePattern) {
                setClimatePattern(newClimatePattern);
            }
        }
    }, [
        defaultCropConfig,
        setPlantingDate,
        setClimatePattern,
        form,
        watchedPlantingDate,
        watchedClimatePattern,
        productIdParam,
    ]);

    useEffect(() => {
        if (soilMoisturesQuery.data && !watchedSoilMoisture) {
            setSoilMoisture(Number(soilMoisturesQuery.data?.[0]?.value));
        }
    }, [soilMoisturesQuery.data, watchedSoilMoisture, setSoilMoisture]);

    const validDates = useMemo(() => getValidPlantingDates({ crop: 'corn' }, fieldQuery.data), [fieldQuery.data]);

    // Result (Used for check if data is available)
    const debouncedEnvironment = useDebounce(form.watch('environment'), 500);
    const debouncedUserDensity = useDebounce(form.watch('userDensity'), 1000);
    const cornDensityCurveQuery = useCornDensityCurveForProducts({
        productsId: form.watch('productId') ? [form.watch('productId')] : undefined,
        environment: debouncedEnvironment,
        minValue: 3,
        maxValue: 12,
        step: 0.05,
        crop: 'corn',
    });

    // Queries for backup data
    const productQuery = useCatalogProductById({
        id: form.watch('productId'),
        crop: 'corn',
    });
    const environmentsQuery = useEnvironments(
        {
            lat: fieldQuery.data?.lat,
            lng: fieldQuery.data?.lng,
            countryCode: country?.code,
            plantingDate: watchedPlantingDate,
            climatePattern: watchedClimatePattern,
            soilMoisture: watchedSoilMoisture,
            crop: 'corn',
            irrigation: undefined,
            soilType: undefined,
        },
        {
            onSuccess: (data) => {
                if (form?.getValues('environment') === 0) form?.setValue('environment', data.default);
            },
        }
    );
    const fertilizationCoefficientsQuery = useFertilizationCoefficientsForProducts({
        environment: debouncedEnvironment,
        products: [
            {
                estimatedYield: cornDensityCurveQuery.data?.[form.watch('productId')]?.recommended?.yield,
                id: form.watch('productId'),
            },
        ],
    });
    const yieldByEnvironmentQuery = useYieldByEnvironmentForProducts({
        environment: debouncedEnvironment,
        productsId: form.watch('productId') ? [form.watch('productId')] : undefined,
        harvestDensity: debouncedUserDensity / 10000,
    });
    const growerQuery = useGetUserNode(fieldQuery.data?.userNode);

    // Create Recommendation Hook
    const appInstance = useSelector(getAppInstance);
    const createRecommendationMutation = useCreateRecommendationMutation();
    const createRecommendation = useCallback(async () => {
        const formValues = form.getValues();
        await createRecommendationMutation.trigger({
            status: 'saved',
            backup: {
                corn_density: {
                    plantingDate: formValues.plantingDate || '',
                    product_profile: formValues.productId,
                    climate_pattern: formValues.climatePattern,
                    soil_moisture: formValues.soilMoisture,
                    irrigation: undefined,
                    soilType: undefined,
                    environment: formValues.environment,
                    recommended_environment: formValues.environment,
                    user_density: formValues.userDensity,
                    user_nitrogen: formValues.userNitrogen,
                    user_planter_efficiency: formValues.successRate,
                },
                otherData: {
                    field: fieldQuery.data,
                    product: productQuery.data,
                    environments: environmentsQuery.data,
                    cornDensityCurve: cornDensityCurveQuery.data,
                    cornDensityCurveQueryIsLoading: cornDensityCurveQuery.isLoading,
                    fertilizationCoefficients: fertilizationCoefficientsQuery.data,
                    yieldByEnvironment: yieldByEnvironmentQuery.data,
                    yieldByEnvironmentQueryIsLoading: yieldByEnvironmentQuery.isLoading,
                    userNode: growerQuery.data,
                },
            },
            weed_v2: {},
            disease: {},
            plague: {},
            weeds: [],
            crops: [],
            field: fieldQuery.data?.id,
            appInstance: appInstance.id,
            corn_density: {
                plantingDate: formValues.plantingDate || '',
                product_profile: formValues.productId,
                climate_pattern: formValues.climatePattern,
                soil_moisture: formValues.soilMoisture,
                irrigation: undefined,
                soilType: undefined,
                environment: formValues.environment,
                recommended_environment: formValues.environment,
                user_density: formValues.userDensity,
                user_nitrogen: formValues.userNitrogen,
                user_planter_efficiency: formValues.successRate,
            },
        });
        mpTrackEvent('Corn Density Recommendation saved', undefined);
        navigate(
            reverse('recommendations.CornDensityRecommendation', {
                params: { recommendationId: createRecommendationMutation.data.id },
            }),
            { replace: true }
        );
    }, [
        form,
        fieldQuery.data,
        appInstance.id,
        createRecommendationMutation,
        navigate,
        productQuery.data,
        environmentsQuery.data,
        cornDensityCurveQuery.data,
        cornDensityCurveQuery.isLoading,
        fertilizationCoefficientsQuery.data,
        yieldByEnvironmentQuery.data,
        yieldByEnvironmentQuery.isLoading,
        growerQuery.data,
    ]);

    const handleCreateCropRecommendation = useCallback(async () => {
        const formValues = form.getValues();
        navigate(
            reverse('recommendations.RecommendationEdit', {
                params: { crop: 'corn', fieldId: fieldQuery.data?.id },
                qs: {
                    planting_date: formValues.plantingDate || '',
                    product_profile: formValues.productId,
                    climate_pattern: formValues.climatePattern,
                    soil_moisture: formValues.soilMoisture,
                },
            }),
            { replace: true }
        );
    }, [form, fieldQuery.data?.id, navigate]);

    const [searchParams] = useSearchParams();
    const userNodeId = searchParams.get('userNodeId') ? Number(searchParams.get('userNodeId')) : undefined;
    const isBayer = useIsValidatedBayerUserType();
    const hasGrower = !userNodeId && isBayer;

    const steps: Step[] = useMemo(() => {
        const handleClickNext = async () => {
            setActiveStep(activeStep + 1);
        };

        const fieldSelection = {
            sidebarName: _('Selección de lote'),
            recommendationType: _('Densidad de siembra'),
            title: _('Elegí el lote para generar la recomendación'),
            subtitle: _(
                'Seleccioná {{fieldText}} para obtener una recomendación ajustada a sus condiciones específicas.',
                { fieldText: hasGrower ? _('el lote de tu productor') : _('el lote') }
            ),
            content: (
                <FieldSelect
                    selectedProduct={Number(productIdParam)}
                    field={field || fieldQuery.data || undefined}
                    setField={(field: Field | undefined) => {
                        if (field) {
                            setField(field);
                            form.setValue('fieldId', field.id);
                        }
                    }}
                    soilMoisture={watchedSoilMoisture}
                    plantingDate={watchedPlantingDate}
                />
            ),
            onClickNext:
                productIdParam && query.get('type') === 'corn_recommendation_view'
                    ? handleCreateCropRecommendation
                    : handleClickNext,
            onClickNextDisabled: () => {
                return !form.getValues().fieldId;
            },
            onClickBackDisabled: () => false,
        };

        const environmentConditions = {
            sidebarName: _('Condiciones ambientales'),
            recommendationType: _('Densidad de siembra'),
            title: _('Ingresá la fecha de siembra y la humededad del suelo'),
            subtitle: _('Con estos datos estimamos cómo rinden distintos productos en {{text}}', {
                text: isUserValidatedRV(user) ? _('los lotes de tus productores') : _('tus lotes'),
            }),
            content: (
                <EnvironmentConditionsStep
                    form={form}
                    soilMoistureChoices={soilMoisturesQuery?.data || []}
                    climatePatternChoices={climatePatternChoices || []}
                    validDates={validDates}
                />
            ),
            onClickNext: handleClickNext,
            onClickNextText: _('Siguiente'),
            onClickNextDisabled: () => {
                return (
                    !form.getValues().plantingDate || !form.getValues().soilMoisture || !form.getValues().climatePattern
                );
            },
            onClickBackDisabled: () => !!productIdParam,
        };

        const productsStep = {
            sidebarName: _('Elegir producto'),
            recommendationType: _('Densidad de siembra'),
            title: _('Elegí el producto que se va a sembrar'),
            content: <ProductsStep form={form} field={fieldQuery.data} />,
            onClickNext: handleClickNext,
            onClickNextText: _('Siguiente'),
            onClickNextDisabled: () => {
                return !form.getValues().productId;
            },
        };

        const reviewAndFinish = {
            sidebarName: _('Revisar y terminar'),
            recommendationType: _('Densidad de siembra'),
            title: _('Revisá y ajustá la recomendación'),
            subtitle: _('Podés modificar el ambiente del lote y el % de logro según el manejo del productor.'),
            content: <CornDensityRecommendationWrapper form={form} />,
            onClickNext: createRecommendation,
            onClickNextText: _('Guardar'),
            onClickNextDisabled: () => {
                return (
                    !form.getValues().environment ||
                    createRecommendationMutation.isMutating ||
                    cornDensityCurveQuery.isValidating ||
                    !cornDensityCurveQuery.data?.[form.getValues('productId')]?.recommended?.density ||
                    false
                );
            },
        };

        if (productIdParam) {
            return [environmentConditions, fieldSelection, reviewAndFinish];
        }

        return [fieldSelection, environmentConditions, productsStep, reviewAndFinish];
    }, [
        field,
        form,
        soilMoisturesQuery?.data,
        climatePatternChoices,
        validDates,
        watchedPlantingDate,
        fieldQuery.data,
        activeStep,
        user,
        createRecommendation,
        hasGrower,
    ]);

    return RecommendationCreate({
        steps,
        activeStep,
        setActiveStep,
    });
}

export default DensityRecommendationCreate;
